import React, { useEffect, useState, useContext } from "react";
import { OrganizationContext } from "../../contexts/OrganizationContextProvider";
import CustomButton from "../CustomButton";
import { updateRisk, getTasks } from "../../api/Api";
import { showToast } from "../../utils/toastUtils";
import { useFormik } from "formik";
import ModalUI from "../ui/ModalUI";
import * as Yup from "yup";
import ShortTextInput from "../Inputs/ShortTextInput";
import TagDropdownInput from "../buttons/TagDropdownInput";
import CustomTemplateDropdown from "../Inputs/CustomTemplateDropdown";
import AddUpdateRiskTemplateModal from "./AddUpdateRiskTemplateModal";
import SelectRiskTemplateModal from "./SelectRiskTemplateModal";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import RichTextEditor from "../texts/RichTextEditor";

function UpdateRiskModel({ show, handleClose, risk ,refresh,workStreamId }) {
  return (
    <ModalUI show={show} handleClose={handleClose} title="Update Risk">
      <UpdateRiskForm
        handleClose={handleClose}
        risk={risk}
        refresh={refresh}
        workStreamId={workStreamId} // pass workStream_Id if needed
      />
    </ModalUI>
  );
}

function UpdateRiskForm({ handleClose, risk,refresh, workStreamId }) {
  const [tasks, setTasks] = useState([]);
  const [taskList, setTaskList] = useState([]);
  const { data: organization } = useContext(OrganizationContext);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [showSelectTemplateModal, setShowSelectTemplateModal] = useState(false);

  // Formik configuration
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: risk.title || "",
      task_ids: risk.tasks.map((task) => task.id) || [],
      description: risk.description || "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      task_ids: Yup.array().min(1, "At least one task is required"),
      description: Yup.string().required("Description is required"),
    }),
    onSubmit: (values, { setSubmitting }) => {
      const riskPayload = {
        title: values.title,
        task_ids: values.task_ids,
        description: values.description,
      };

      updateRisk(risk.id, riskPayload)
        .then((response) => {
          if (response.status === 200) {
            showToast("Risk updated successfully", "success");
            refresh(); // Refresh the risk list after update
            handleClose(); // Close the modal on success
          }
        })
        .catch((error) => {
          showToast("Failed to update Risk", "error");
          console.error(error);
        })
        .finally(() => setSubmitting(false));
    },
  });


  // Fetch tasks and map selected tasks
  useEffect(() => {
  
    getTasks({ organization_id: organization.current_organization.id , workStream_id: workStreamId })
      .then((response) => {
        if (response.status === 200) {
          const formattedTasks = response.data.map((task) => ({
            ...task,
            id: task.id,
            name: task.title,
          }));
          setTaskList(formattedTasks);

          const selectedTasks = risk.tasks.map((task) => ({
            id: task.id,
            name: task.title,
          }));
          setTasks(selectedTasks);
        }
      })
      .catch((error) => {
        showToast("Failed to fetch tasks", "error");
        console.error(error);
      });
  }, [organization?.current_organization?.id,]);

  const handleTaskChange = (event, newValue) => {
    setTasks(newValue);
    formik.setFieldValue(
      "task_ids",
      newValue.map((task) => task.id)
    );
  };


  const dropdownItems = [
    {
      label: "Use Template",
      onClick: () => setShowSelectTemplateModal(true),
    },
    {
      label: "Create Template",
      onClick: () => setShowAddTemplateModal(true),
    },
  ];



  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="text-start">
        <ShortTextInput
          label="Title"
          type="text"
          id="title"
          required
          value={formik.values.title}
          onChange={formik.handleChange}
          placeholder="Enter Risk title"
          className={`form-control ${
            formik.touched.title && formik.errors.title ? "is-invalid" : ""
          }`}
          error={formik.touched.title && formik.errors.title}
        />
      </div>

      {/* Task Selection */}
      <div className="mt-3">
        <div className="my-3">
          <TagDropdownInput
            label="Tasks"
            placeholder="Select Tasks"
            required
            data={taskList}
            selectedItems={tasks}
            onChange={handleTaskChange}
            error={formik.touched.task_ids && formik.errors.task_ids}
          />
        </div>
      </div>

      {/* Description with Dropdown */}
      <div className="position-relative">
        <div className="position-absolute end-0 top-0 m-1">
          <CustomTemplateDropdown icon={HiOutlineDotsHorizontal} items={dropdownItems} />
        </div>
        <RichTextEditor
          label="Description"
          id="description"
          placeholder="Enter Risk description..."
          required
          editorValue={formik.values.description}
          setEditorValue={(value) => formik.setFieldValue("description", value)}
          error={formik.touched.description && formik.errors.description}
        />
      </div>

      {/* Template Modals */}
      {showSelectTemplateModal && (
        <SelectRiskTemplateModal
          show={showSelectTemplateModal}
          handleClose={() => setShowSelectTemplateModal(false)}
          handleUse={(template) => {
            formik.setFieldValue("description", template.description);
            setShowSelectTemplateModal(false);
          }}
        />
      )}

      {showAddTemplateModal && (
        <AddUpdateRiskTemplateModal
          show={showAddTemplateModal}
          handleClose={() => setShowAddTemplateModal(false)}
          defaultDescription={formik.values.description}
          refresh={() => {}}
        />
      )}

      {/* Submit Button */}
      <div className="d-flex justify-content-center mt-3">
        <CustomButton type="submit" disabled={formik.isSubmitting}>
          {formik.isSubmitting ? "Updating..." : "Update"}
        </CustomButton>
      </div>
    </form>
  );
}

export default UpdateRiskModel;
